import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Section from '../../components/ui/Section';
import { Link } from 'react-router-dom';

const personas = [
  {
    id: 'viktoria',
    name: 'Die erschöpfte Leaderin',
    age: 'Viktoria, 45',
    tag: 'Corporate & Leadership',
    pain: 'Du hast alles erreicht – und fühlst trotzdem innere Leere.',
    desire: 'Führen aus der weiblichen Intuition. Ohne Burnout.',
    trigger: 'Feminine Leadership',
    to: '/female-leadership-fuer-frauen',
    color: 'rgba(212,175,55,0.08)',
    border: 'rgba(212,175,55,0.3)',
  },
  {
    id: 'isabel',
    name: 'Die spirituelle Suchende',
    age: 'Isabel, 33',
    tag: 'Beziehung & Dualseelen',
    pain: 'Du steckst in einer Verbindung fest, die sich wie Heimat und Schlachtfeld zugleich anfühlt.',
    desire: 'Energetische Heilung. Emotionale Souveränität.',
    trigger: 'Love Code',
    to: '/beziehungsheilung-fuer-frauen',
    color: 'rgba(114,47,55,0.08)',
    border: 'rgba(155,66,78,0.3)',
  },
  {
    id: 'sophie',
    name: 'Die mutige Gründerin',
    age: 'Sophie, 28',
    tag: 'Soul Business',
    pain: 'Dein Traum ist da – aber Sichtbarkeit macht Dir Angst.',
    desire: 'Ein Business, das sich wie Du anfühlt.',
    trigger: 'Business Code',
    to: '/female-leadership-fuer-frauen',
    color: 'rgba(212,175,55,0.06)',
    border: 'rgba(212,175,55,0.2)',
  },
  {
    id: 'renate',
    name: 'Die transformative Neustart-Frau',
    age: 'Renate, 54',
    tag: 'Göttliche Fülle',
    pain: 'Die Kinder sind aus dem Haus. Jetzt bist Du an der Reihe.',
    desire: 'Dein nächstes Kapitel – voller Bedeutung und Leichtigkeit.',
    trigger: 'Transformation',
    to: '/programme',
    color: 'rgba(212,175,55,0.07)',
    border: 'rgba(212,175,55,0.25)',
  },
];

const PersonaCard = ({ p, isActive, onClick }) => (
  <motion.div
    onClick={onClick}
    whileHover={{ y: -4 }}
    className="cursor-pointer p-8 rounded-sm border transition-all duration-500 relative overflow-hidden"
    style={{
      background: isActive ? p.color : 'rgba(255,255,255,0.02)',
      borderColor: isActive ? p.border : 'rgba(255,255,255,0.08)',
      boxShadow: isActive ? `0 0 40px ${p.color}` : 'none',
    }}
  >
    <p className="text-xs uppercase tracking-widest text-gold/70 mb-2">{p.tag}</p>
    <h3 className="text-xl font-serif text-cream mb-1">{p.name}</h3>
    <p className="text-cream/45 text-sm font-light">{p.age}</p>
    {isActive && (
      <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} className="absolute bottom-0 left-0 right-0 h-[2px] origin-left"
        style={{ background: `linear-gradient(to right, ${p.border}, transparent)` }} />
    )}
  </motion.div>
);

const Personas = () => {
  const [active, setActive] = useState(0);
  const p = personas[active];

  return (
    <Section className="bg-darker border-t border-white/5">
      <div className="text-center mb-16">
        <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
          className="text-gold tracking-[0.2em] uppercase text-sm mb-4">
          Erkennst Du Dich?
        </motion.p>
        <h2 className="text-4xl md:text-5xl font-serif text-cream">
          Für welche Frau bist Du <span className="italic text-gradient-gold">hier?</span>
        </h2>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
        {personas.map((persona, i) => (
          <PersonaCard key={persona.id} p={persona} isActive={active === i} onClick={() => setActive(i)} />
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={active}
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.5 }}
          className="glass-panel p-10 md:p-16 relative overflow-hidden"
        >
          <div className="absolute top-0 left-0 right-0 h-[1px]"
            style={{ background: `linear-gradient(to right, transparent, ${p.border}, transparent)` }} />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 items-center">
            <div className="md:col-span-2 space-y-6">
              <div>
                <p className="text-xs uppercase tracking-widest text-cream/40 mb-2">Dein Schmerzpunkt</p>
                <p className="text-2xl md:text-3xl font-serif text-cream leading-snug italic">"{p.pain}"</p>
              </div>
              <div className="w-full h-[1px] bg-white/5" />
              <div>
                <p className="text-xs uppercase tracking-widest text-cream/40 mb-2">Dein Zielzustand</p>
                <p className="text-lg text-cream/80 font-light leading-relaxed">{p.desire}</p>
              </div>
            </div>
            <div className="flex flex-col items-start md:items-center gap-4">
              <p className="text-xs uppercase tracking-widest text-cream/40">Dein Portal</p>
              <Link to={p.to}>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}
                  className="px-8 py-5 border text-gold font-sans tracking-widest uppercase text-sm relative overflow-hidden group"
                  style={{ borderColor: p.border }}
                >
                  <span className="relative z-10">{p.trigger} entdecken →</span>
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{ background: p.color }} />
                </motion.div>
              </Link>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </Section>
  );
};

export default Personas;